import { makeCanvasProbe, type CanvasProbe } from './canvasProbe.js';
import type { PlaygroundVirtualEvent } from '../types.js';

/**
 * The DSL's executor — `defineSandboxTest`'s step semantics as the playground's virtual timing mode: `runSandboxTest`'s
 * body from the booted game onward, moved verbatim from @gotti/sandbox-test (setup, the step loop, `until` and its
 * rebase, sides, settle, asserts, the `[label]` wrapping and teardown). It never owns the clock: its host installs the
 * virtual clock and pumps it (each `harness.tick`), and hands in what is the host's — the input driver, cancellation,
 * the DSL's own error classes, the latency it booted with and a wall-clock delay — so every DSL result stays what it was.
 * Each step's progress goes to the host as it runs (`onEvent`), which is how the playground's runner shows the steps.
 */

// Structural twins of @gotti/sandbox-test's DSL declarations, which sandbox-test keeps declaring for the AI's type
// surface (virtual.test-d.ts proves each assignable to the other, both ways).

export type ValidKey = string;

/** A per-test value bag shared across every step and both sides of one test. */
export type SandboxTestStore = Record<string, any>;

/** The client a step targets, passed last to every side callback. */
export interface TestClient<Api = any> {
  id: string;
  index: number;
  api: Api;
  press: (key: ValidKey) => void;
  getPressingKey: () => ValidKey | null;
  getPressedKeys: () => ValidKey[];
  disconnect: () => void | Promise<void>;
}

/** One side (`client` or `server`) of a step. */
export interface SandboxTestSide<Api = any> {
  press?:
    | ValidKey
    | ValidKey[]
    | ((api: Api, store: SandboxTestStore, client: TestClient) => ValidKey | ValidKey[] | null | undefined);
  click?: { x: number; y: number };
  type?: string;
  disconnect?: boolean;
  act?: (api: Api, store: SandboxTestStore, client: TestClient) => void | Promise<void>;
  assert?: (api: Api, canvas: CanvasProbe, store: SandboxTestStore, client: TestClient) => void | Promise<void>;
}

/** A step's `until`: polled once per tick from its `atTime` until `cond` holds or `maxMs` (virtual) runs out. */
export interface StepUntilOptions<ServerApi = any> {
  cond: (serverApi: ServerApi, store: SandboxTestStore) => boolean;
  maxMs?: number;
  label?: string;
}

/** One timed step of a DSL test. */
export interface VirtualTestStep<ClientApi = any, ServerApi = any> {
  atTime: number;
  label: string;
  clientIndex?: number;
  client?: SandboxTestSide<ClientApi>;
  server?: SandboxTestSide<ServerApi>;
  until?: StepUntilOptions<ServerApi>;
}

/** `SetupHandle.until`'s options. */
export interface SetupUntilOptions {
  maxMs?: number;
  stepMs?: number;
  drive?: () => void;
  label?: string;
}

/** What a test's `setup.run` gets: gameplay-only input on the same virtual clock the steps run on. */
export interface SetupHandle<ClientApi = any, ServerApi = any> {
  serverApi: ServerApi;
  clientApi(index?: number): ClientApi;
  clientId(index?: number): string;
  press(key: string, clientIndex?: number): void;
  click(x: number, y: number, clientIndex?: number): void;
  type(text: string, clientIndex?: number): void;
  tick(ms: number): Promise<void>;
  until(cond: () => boolean, opts?: SetupUntilOptions): Promise<void>;
  store: Record<string, unknown>;
}

/** A DSL test, as `defineSandboxTest` declares it. */
export interface VirtualTest<ClientApi = any, ServerApi = any> {
  label: string;
  clients?: number;
  latency?: number;
  steps: VirtualTestStep<ClientApi, ServerApi>[];
  setup?: { label: string; run: (h: SetupHandle<ClientApi, ServerApi>) => void | Promise<void> };
  timeScale?: number;
  timeoutMs?: number;
  realtime?: boolean;
  assertionMode?: { runSequentially?: boolean; failFast?: boolean };
}

/** A game booted for one DSL test: its tick, its clients' and server's `$api`, their canvases, and its teardown. */
export interface BootedGame {
  harness: any;
  disconnect?: (clientIndex: number) => void | Promise<void>;
  clientApi: any;
  clientApis?: any[];
  serverApi: any;
  canvas: HTMLCanvasElement | null;
  canvases?: (HTMLCanvasElement | null)[];
  clientIds?: string[];
  destroy: () => void | Promise<void>;
}

/** How a key, a click or typed text reaches a client — each runner's own. */
export interface InputDriver {
  press: (key: string, clientIndex?: number) => void;
  click: (x: number, y: number, clientIndex?: number) => void;
  type: (text: string, clientIndex?: number) => void;
}

/** What the executor's host hands in: everything about a run that is the host's, not the DSL's. */
export interface VirtualTestOptions {
  /** The runner's input driver; `null` drops `press`/`click`/`type` (the DSL still records a test's presses). */
  input: InputDriver | null;
  /** Whether the run was cancelled — checked before each tick of the setup, the way to a step and an `until`. */
  cancelled: () => boolean;
  /** The error a cancelled run ends the test with. */
  cancelledError: () => Error;
  /** The latency the game was booted with (ms, clamped): an interacting step settles 2 extra ticks per latency window. */
  latencyMs: number;
  /** The error an `until` that never holds throws, from its `UNTIL FAILED: …` message. */
  untilFailed: (message: string) => Error;
  /** The error a setup `until` that never holds throws, from its `SETUP FAILED: …` message. */
  setupFailed: (message: string) => Error;
  /** A wall-clock delay — the real `setTimeout`, never the virtual clock's — for a `realtime: true` test's ticks. */
  realDelay: (ms: number) => Promise<void>;
  /**
   * Hears each step as it runs — `step` is its index in run order (by `atTime`, ties in declared order): it starts
   * (its time has come and its `until` held), its client side and then its server side are applied, it settles, its
   * client side's assert and then its server side's pass (`asserted`, for a side that asserts), and it passes; or
   * `failed`, once, when the step ends the test. What the playground's runner maps onto the progress tree.
   */
  onEvent?: (event: PlaygroundVirtualEvent) => void;
}

const TICK_SECONDS = 1 / 60;            // delta passed to systems — matches live runtime timeStep
const TICK_MS = TICK_SECONDS * 1000;    // ~16.667ms — used for atTime (ms) accounting

/** Runs `test` against the booted `game` — every step, its settle and asserts — and tears the game down. */
export async function runVirtualTest(test: VirtualTest, game: BootedGame, options: VirtualTestOptions): Promise<void> {
  const input = options.input;
  // A cancelled run stops at its next tick: checked before each tick of the setup, the way to a step's time and a
  // step's `until` — outside the step's own `[label]`-wrapping — and never in a step's short settle.
  const throwIfCancelled = (): void => {
    if (options.cancelled()) throw options.cancelledError();
  };
  // Resolve a step's target client api/canvas, falling back to the legacy
  // single-client fields so a booter that doesn't populate the indexed arrays
  // (or a step targeting index 0) behaves exactly as before.
  const clientApiFor = (idx: number) => game.clientApis?.[idx] ?? game.clientApi;
  const canvasFor = (idx: number) => game.canvases?.[idx] ?? game.canvas;
  const timeScale = test.timeScale ?? 1;
  const steps = [...test.steps].sort((a, b) => a.atTime - b.atTime);
  let simTime = 0;
  // One value bag per test, shared across every step + both sides.
  const store: SandboxTestStore = {};
  // Per-client log of every key the TEST pressed (static, callback-returned,
  // or via client.press) — backs TestClient.getPressingKey/getPressedKeys.
  const pressedByClient = new Map<number, ValidKey[]>();
  const recordPress = (idx: number, key: ValidKey): void => {
    const log = pressedByClient.get(idx) ?? [];
    log.push(key);
    pressedByClient.set(idx, log);
  };
  const makeTestClient = (idx: number): TestClient => ({
    id: game.clientIds?.[idx] ?? `player-${idx + 1}`,
    index: idx,
    api: clientApiFor(idx),
    press: (key) => {
      if (input) input.press(key, idx);
      recordPress(idx, key);
    },
    getPressingKey: () => {
      const log = pressedByClient.get(idx);
      return log && log.length > 0 ? log[log.length - 1] : null;
    },
    getPressedKeys: () => [...(pressedByClient.get(idx) ?? [])],
    disconnect: () => game.disconnect?.(idx),
  });
  // Whether the steps failed: a teardown that throws on top of a failure must not replace the test's own message.
  let stepsFailed = false;
  // A teardown that threw after steps that passed: the test's failure, raised once the teardown is over.
  let teardownError: { error: unknown } | undefined;
  try {
    // ── Imperative setup phase (optional) ─────────────────────────────
    // Runs after boot + joins, before the declarative timeline. Consumes
    // virtual time on the SAME clock; step atTimes are relative to setup
    // completion (t=0 = setup done). Drivers are gameplay-only: they read via
    // $api and act via the same input path steps use — no privileged setters.
    if (test.setup) {
      const tickOnce = async (): Promise<void> => {
        throwIfCancelled();
        await game.harness.tick(TICK_SECONDS);
        if (test.realtime) await options.realDelay(TICK_MS);
        simTime += TICK_MS;
      };
      const setupTick = async (ms: number): Promise<void> => {
        const target = simTime + ms;
        while (simTime < target) await tickOnce();
      };
      const h: SetupHandle = {
        serverApi: game.serverApi,
        clientApi: (index = 0) => clientApiFor(index),
        clientId: (index = 0) => game.clientIds?.[index] ?? `player-${index + 1}`,
        press: (key, clientIndex = 0) => makeTestClient(clientIndex).press(key as ValidKey),
        click: (x, y, clientIndex = 0) => input?.click(x, y, clientIndex),
        type: (text, clientIndex = 0) => input?.type(text, clientIndex),
        tick: setupTick,
        until: async (cond, opts = {}) => {
          const maxMs = opts.maxMs ?? 30_000;
          const stepMs = opts.stepMs ?? TICK_MS;
          const started = simTime;
          for (;;) {
            if (cond()) return;
            if (simTime - started >= maxMs) {
              throw options.setupFailed(
                `SETUP FAILED: ${opts.label ?? test.setup!.label}: not established within ${maxMs}ms (virtual)`,
              );
            }
            opts.drive?.();
            await setupTick(stepMs);
          }
        },
        store,
      };
      await test.setup.run(h);
    }
    const setupEndTime = simTime;
    // Rebased per-timeline origin: starts at setup's end, exactly like today.
    // A step with `until` rebases this to its own completion once it's done,
    // so later steps' `atTime`s measure from THAT step instead — a test with
    // no `until` steps never rebases, so its tick sequence is byte-identical
    // to before this feature existed.
    let timeOrigin = setupEndTime;
    const report = (step: number, kind: Exclude<PlaygroundVirtualEvent['kind'], 'asserted'>): void => options.onEvent?.({ step, kind });
    const reportAsserted = (step: number, side: 'client' | 'server'): void => options.onEvent?.({ step, kind: 'asserted', side });
    for (const [stepIndex, step] of steps.entries()) {
      try {
        const idx = step.clientIndex ?? 0;
        const target = timeOrigin + step.atTime * timeScale;
        while (simTime < target) {
          throwIfCancelled();
          // AWAIT: the harness booter's tick is synchronous (await is a no-op), but
          // the real-runtime booter's tick is `advance()` (async) — un-awaited
          // advances would overlap and corrupt the shared virtual clock.
          await game.harness.tick(TICK_SECONDS);
          if (test.realtime) await options.realDelay(TICK_MS);
          simTime += TICK_MS;
        }
        // Step-level `until`: poll the condition ONCE PER TICK (checking once
        // immediately at `target`, before any further tick) until it holds or
        // the virtual-ms budget (measured from `target`) runs out. Kept OUTSIDE
        // the try/catch below (like the tick-to-target loop above it) so a
        // thrown UntilFailedError propagates with its exact, unwrapped message
        // — never prefixed with `[step.label]` the way an in-step failure is.
        if (step.until) {
          const { cond, maxMs = 30_000, label: untilLabel } = step.until;
          for (;;) {
            if (cond(game.serverApi, store)) break;
            if (simTime - target >= maxMs) {
              throw options.untilFailed(
                `UNTIL FAILED: ${untilLabel ?? step.label}: condition not met within ${maxMs}ms (virtual)`,
              );
            }
            throwIfCancelled();
            await game.harness.tick(TICK_SECONDS);
            if (test.realtime) await options.realDelay(TICK_MS);
            simTime += TICK_MS;
          }
        }
        const testClient = makeTestClient(idx);
        report(stepIndex, 'start');
        try {
          await applySide(step.client, clientApiFor(idx), idx, store, testClient, recordPress, input);
          if (step.client?.disconnect && game.disconnect) await game.disconnect(idx);
          report(stepIndex, 'client');
          await applySide(step.server, game.serverApi, undefined, store, testClient, recordPress, input);
          report(stepIndex, 'server');
          // Settle long enough for this step's input/act to PROPAGATE before we assert.
          // The old synchronous harness delivered in ONE tick, so one settle tick
          // sufficed. The REAL runtime is tick-quantized + latency-delayed: a client
          // input dispatches on the next client `onUpdate`, crosses the transport
          // (`latencyMs`), and is applied on the server's next `onUpdate` — and a
          // client-side assert also needs the server broadcast back. A single tick can
          // read PRE-input state and spuriously fail (the 1-tick-late regression the
          // real runtime introduced). Budget a full client↔server round-trip after an
          // interaction (~3 ticks at latency 0, +2 ticks per latency-window); an
          // assert-only step needs no propagation and keeps a single settle tick.
          const latencyTicks = Math.ceil((options.latencyMs || 0) / TICK_MS);
          const interacted =
            !!(step.client && (step.client.press !== undefined || step.client.click || step.client.type !== undefined || step.client.act || step.client.disconnect)) ||
            !!(step.server && step.server.act);
          const settleTicks = interacted ? 3 + 2 * latencyTicks : 1;
          for (let s = 0; s < settleTicks; s++) {
            await game.harness.tick(TICK_SECONDS); // await: real booter's tick is async
            if (test.realtime) await options.realDelay(TICK_MS);
            simTime += TICK_MS;
          }
          report(stepIndex, 'settled');
          await assertSide(step.client, clientApiFor(idx), canvasFor(idx), store, testClient);
          if (step.client?.assert) reportAsserted(stepIndex, 'client');
          await assertSide(step.server, game.serverApi, canvasFor(idx), store, testClient);
          if (step.server?.assert) reportAsserted(stepIndex, 'server');
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          throw new Error(`[${step.label}] ${msg}`);
        }
        report(stepIndex, 'passed');
        // REBASE: an `until` step just finished (sides + settle + assert) — later
        // steps' `atTime`s now measure from HERE, not from `setupEndTime`/the
        // previous origin. Ordinary steps never touch `timeOrigin`.
        if (step.until) timeOrigin = simTime;
      } catch (err) {
        // Whatever ended the test on this step — its time never came (a cancellation), its `until` never held, a side or
        // an assert threw — is the step's failure; the error itself goes on unchanged.
        report(stepIndex, 'failed');
        throw err;
      }
    }
  } catch (err) {
    stepsFailed = true;
    throw err;
  } finally {
    try {
      await game.destroy();
    } catch (error) {
      // The test's outcome is its steps': after a failure, a teardown error is reported beside it, never instead.
      if (stepsFailed) console.warn('[sandbox-test] the game threw while it was torn down after a failed test:', error);
      else teardownError = { error };
    }
  }
  // After steps that passed, a teardown that threw is the test's failure (raised here, not from the `finally`).
  if (teardownError) throw teardownError.error;
}


async function applySide(
  side: SandboxTestSide<any> | undefined,
  api: any,
  clientIndex: number | undefined,
  store: SandboxTestStore,
  client: TestClient,
  recordPress: (idx: number, key: ValidKey) => void,
  input: InputDriver | null,
): Promise<void> {
  if (!side) return;
  if (side.press !== undefined && input) {
    const resolved = typeof side.press === 'function' ? side.press(api, store, client) : side.press;
    if (resolved !== null && resolved !== undefined) {
      const keys = Array.isArray(resolved) ? resolved : [resolved];
      for (const k of keys) {
        input.press(k, clientIndex);
        recordPress(client.index, k);
      }
    }
  }
  if (side.click && input) input.click(side.click.x, side.click.y, clientIndex);
  if (side.type !== undefined && input) input.type(side.type, clientIndex);
  if (side.act) await side.act(api, store, client);
}

async function assertSide(
  side: SandboxTestSide<any> | undefined,
  api: any,
  canvas: HTMLCanvasElement | null,
  store: SandboxTestStore,
  client: TestClient,
): Promise<void> {
  if (!side?.assert) return;
  await side.assert(api, makeCanvasProbe(canvas), store, client);
}
